import { ChangeEvent, FC, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { MiDetailFields } from "@miplatform/components";
import {
  EMAIL_TEMPLATE_TYPE_TO_LABEL_MAP,
  ENTITY_TYPES,
  FILE_MIME_TYPE_EXTENSIONS,
  INPUT_TYPES,
} from "@miplatform/constants";
import {
  IStepTemplateData,
  StepDisplayConfigButtonType,
  StepType,
} from "@miplatform/types";
import { useDocumentCategories, useEmailTemplate } from "@mibuildweb/hooks";
import { IWorkOrderTemplateData } from "../../WorkOrderTemplates/ConfigWorkOrderTemplates.types";
import { ConfigAccordionHeader } from "../../components/ConfigAccordionHeader/ConfigAccordionHeader";

export const WorkOrderStepDetailsTabView: FC<
  WorkOrderStepDetailsTabViewProps
> = ({ form, extraTabProps }) => {
  const { control, watch, setValue } = form;
  const { workOrderTemplateDetails, isReadOnly } = extraTabProps || {};
  const { stepType, documentCategoryId, isRequired, displayConfig } = watch();

  const {
    getDocumentCategoriesByType,
    setDocumentCategories,
    documentCategories,
  } = useDocumentCategories();
  const { getEmailTemplatesByOrderTemplateId, emailTemplateDetails } =
    useEmailTemplate();

  const fetchDocumentCategories = async () => {
    const response = await getDocumentCategoriesByType(ENTITY_TYPES.STEP);
    setDocumentCategories(
      response.data.map((c: { name: string; id: string }) => ({
        ...c,
        label: c.name,
        value: c.id,
      }))
    );
  };

  useEffect(() => {
    fetchDocumentCategories();
  }, []);

  useEffect(() => {
    if (workOrderTemplateDetails) {
      getEmailTemplatesByOrderTemplateId(
        workOrderTemplateDetails?.orderTemplateId
      );
    }
  }, [workOrderTemplateDetails]);

  const getStepTypeFields = () => {
    const { allowedFileFormats } =
      documentCategories?.find(
        (category) => category.id === documentCategoryId
      ) || {};

    const fields = [
      {
        label: "Step Name",
        name: "title",
        required: true,
        formControl: control,
        disabled: isReadOnly,
      },
      {
        label: "Step Type",
        name: "stepType",
        required: true,
        fieldType: INPUT_TYPES.SELECT,
        formControl: control,
        disabled: isReadOnly,
        options: Object.values(StepType).map((type) => ({
          label: type,
          value: type,
        })),
        setValue: (e: ChangeEvent<HTMLInputElement>) => {
          const { value } = e.target;
          setValue("stepType", value, {
            shouldDirty: true,
            shouldValidate: true,
          });
          if (value !== StepType.Document) {
            setValue("documentCategoryId", null, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
          if (value !== StepType.Email) {
            setValue("emailType", null, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
        },
      },
      {
        label: "Work Order",
        value: workOrderTemplateDetails?.name,
        disabled: true,
        required: true,
      } as any,
    ];

    if (stepType === StepType.Email) {
      fields.push({
        label: "Email Template Type",
        name: "emailType",
        fieldType: INPUT_TYPES.SELECT,
        formControl: control,
        options: emailTemplateDetails?.map((t: any) => ({
          label:
            EMAIL_TEMPLATE_TYPE_TO_LABEL_MAP[
              t.emailType as keyof typeof EMAIL_TEMPLATE_TYPE_TO_LABEL_MAP
            ],
          value: t.emailType,
        })),
        width: "100%",
        required: true,
        disabled: isReadOnly,
      });
    }

    if (stepType === StepType.Document) {
      fields.push(
        {
          label: "Document Category",
          name: "documentCategoryId",
          fieldType: INPUT_TYPES.SELECT,
          formControl: control,
          options: documentCategories,
          width: "50%",
          required: true,
          disabled: isReadOnly,
        },
        {
          label: "Document Type",
          width: "50%",
          disabled: true,
          readOnly: true,
          textTruncate: false,
          value: allowedFileFormats
            ?.map((format) => FILE_MIME_TYPE_EXTENSIONS[format])
            .filter(Boolean)
            .join(", ")
            .toUpperCase(),
        }
      );
    }

    return fields;
  };

  const getStepOptionsFields = () => {
    const fields = [
      {
        label: "Description",
        name: "description",
        formControl: control,
        width: "100%",
        disabled: isReadOnly,
      },
      {
        label: "Has Button",
        fieldType: INPUT_TYPES.CHECKBOX,
        value: displayConfig?.hasButton,
        name: "displayConfig.hasButton",
        width: "25%",
        disabled: isReadOnly,
        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
          if (e.target.checked) {
            setValue(
              "displayConfig",
              {
                hasButton: true,
                title: "linkUrl",
                content: "",
                buttonType: null,
                buttonLabel: "",
              },
              { shouldDirty: true, shouldValidate: true }
            );
          } else {
            setValue(
              "displayConfig",
              {
                hasButton: false,
                title: "",
                content: "",
                buttonType: null,
                buttonLabel: "",
              },
              { shouldDirty: true, shouldValidate: true }
            );
          }
        },
      } as any,
      {
        label: "Optional Step",
        value: !isRequired,
        fieldType: INPUT_TYPES.CHECKBOX,
        width: "25%",
        disabled: isReadOnly,
        handleChange: (e: ChangeEvent<HTMLInputElement>) =>
          setValue("isRequired", !e.target.checked, {
            shouldDirty: true,
            shouldValidate: true,
          }),
      },
      {
        fieldType: INPUT_TYPES.CUSTOM,
        customComponent: <div />,
        width: "25%",
      },
      {
        fieldType: INPUT_TYPES.CUSTOM,
        customComponent: <div />,
        width: "25%",
      },
    ];

    if (displayConfig?.hasButton) {
      fields.push(
        {
          label: "Link Type",
          name: "displayConfig.buttonType",
          fieldType: INPUT_TYPES.SELECT,
          formControl: control,
          required: true,
          disabled: isReadOnly,
          options: [
            {
              label: "Button",
              value: StepDisplayConfigButtonType.Link,
            },
            {
              label: "Copy To Clipboard",
              value: StepDisplayConfigButtonType.CopyToClipboard,
            },
          ],
        },
        {
          label: "Button Label",
          name: "displayConfig.buttonLabel",
          formControl: control,
          required: displayConfig.buttonType === "Link",
          disabled: isReadOnly,
        },
        {
          label: displayConfig.buttonType === "Link" ? "Link" : "Content",
          name: "displayConfig.content",
          formControl: control,
          required: true,
          disabled: isReadOnly,
          setValue: (e: ChangeEvent<HTMLInputElement>) => {
            setValue("displayConfig.content", e.target.value, {
              shouldDirty: true,
              shouldValidate: true,
            });
          },
        }
      );
    }

    return fields;
  };

  return (
    <ConfigAccordionHeader title="Step Details">
      <MiDetailFields detailFields={getStepTypeFields()} />
      <MiDetailFields detailFields={getStepOptionsFields()} />
    </ConfigAccordionHeader>
  );
};

interface WorkOrderStepDetailsTabViewProps {
  form: UseFormReturn<IStepTemplateData>;
  extraTabProps?: {
    isReadOnly?: boolean;
    workOrderTemplateDetails?: IWorkOrderTemplateData;
  };
}
